// エージェント（チャット）セッションの結線。
//
// 役割: App.jsx から切り出したエージェント結線フック。チャット状態（messages / isRunning /
//       chatInput）と会話永続化（ConversationStore）を所有し、runAgent へ callClaude /
//       toolRegistry / system を注入して実行する。onEvent で「途中経過→チャット」
//       「ツール実行→ログ」を出し分ける（e-Stat 流）。生成/削除レイヤーの地図反映は
//       注入された addLayerToMap / dropLayerFromMap が担う。
// 注入: { addLayerToMap, dropLayerFromMap, layers, dataTables, apiKey, model, maxTokens, log }。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { runAgent } from '../agent/runtime'
import { callClaude } from '../agent/claude-client'
import { composeSystemPrompt } from '../agent/system-prompt'
import { ConversationStore } from '../agent/conversation-store'
import { createSpatialToolRegistry } from '../tools/register-tools'
import { formatDataTablesForPrompt } from '../data/data-prompt'

// モジュールスコープの単一インスタンス。
const conversationStore = new ConversationStore()
const SYSTEM_PROMPT = composeSystemPrompt()
// ツールが来歴（origin_prompt）に記録する現在のプロンプト。送信ごとに更新する。
const agentSession = { originPrompt: '' }
const CHAT_VIEW_STORAGE = 'web-gis-agent.chat-view'

function loadChatView() {
  try {
    const parsed = JSON.parse(globalThis.localStorage?.getItem(CHAT_VIEW_STORAGE) ?? '[]')
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

// 現在のレイヤー一覧とデータテーブルをシステムプロンプトへ注入する
// （コンテキストメモリ, plan.md §8）。
//
// プロンプトキャッシュはプレフィックス一致で効くため、毎回変わる「現在のレイヤー一覧」を
// 安定コンテンツと同じブロックに混ぜると、レイヤー操作のたびに BASE+スキル+カタログまで
// キャッシュミスする。そこで「安定プレフィックス」と「揮発するレイヤー一覧」を別ブロックに分け、
// それぞれに cache_control を置いて安定部分を再利用できるようにする。
function buildSystemWithLayers(layers, dataTables) {
  // 安定プレフィックス（BASE+スキル+カタログ）= 毎回同じ → ここをキャッシュ。
  let stable = SYSTEM_PROMPT
  const blocks = [{ type: 'text', text: stable, cache_control: { type: 'ephemeral' } }]
  // 揮発部分（現在のレイヤー一覧）= 変わってもこのブロックだけ差し替わる。後続に置く。
  if (layers.length) {
    const list = layers
      .map((l) => `- ${l.layerId}（${l.geomType ?? '?'}, ${l.featureCount} 件）`)
      .join('\n')
    blocks.push({
      type: 'text',
      text: `## 現在のレイヤー\n${list}`,
      cache_control: { type: 'ephemeral' },
    })
  }
  // 揮発部分（アップロード済みデータテーブル）= JOIN 対象として注入する。
  const dataBlock = formatDataTablesForPrompt(dataTables)
  if (dataBlock) {
    blocks.push({ type: 'text', text: dataBlock, cache_control: { type: 'ephemeral' } })
  }
  return blocks
}

// ツール入力をログ用に短く文字列化する。
function shortInput(input) {
  try {
    const s = JSON.stringify(input)
    return s.length > 80 ? `${s.slice(0, 80)}…` : s
  } catch {
    return ''
  }
}

export function useAgentSession({
  addLayerToMap,
  dropLayerFromMap,
  setLayerStyle,
  layers,
  dataTables,
  apiKey,
  model,
  maxTokens,
  log,
}) {
  // --- チャット / エージェント ---
  const [messages, setMessages] = useState(loadChatView)
  const [isRunning, setIsRunning] = useState(false)
  const abortRef = useRef(null)
  // チャット入力は App が保持（レイヤー行の「@」から id を差し込めるようにするため）。
  const [chatInput, setChatInput] = useState('')
  const chatInputRef = useRef(null)

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(CHAT_VIEW_STORAGE, JSON.stringify(messages))
    } catch {
      // 容量超過などでも現在の画面上の会話は継続する。
    }
  }, [messages])

  // エージェント用ツールレジストリ（生成/削除を地図へ反映するコールバックを注入）。
  const toolRegistry = useMemo(
    () =>
      createSpatialToolRegistry({
        addLayerToMap,
        dropLayerFromMap,
        setLayerStyle,
        session: agentSession,
      }),
    [addLayerToMap, dropLayerFromMap, setLayerStyle],
  )

  const handleSubmit = useCallback(
    async (content) => {
      if (!apiKey || isRunning) return
      setMessages((cur) => [...cur, { id: crypto.randomUUID(), role: 'user', content }])
      setIsRunning(true)
      const controller = new AbortController()
      abortRef.current = controller
      agentSession.originPrompt = content
      try {
        const result = await runAgent({
          instruction: content,
          messages: conversationStore.getMessages(),
          toolRegistry,
          system: buildSystemWithLayers(layers, dataTables),
          signal: controller.signal,
          callModel: (req) => callClaude({ ...req, apiKey, model, maxTokens }),
          onEvent: (event) => {
            // 途中経過はチャットへ、ツール実行はログへ出し分ける（e-Stat 流）。
            if (event.type === 'assistant_text') {
              setMessages((cur) => [
                ...cur,
                { id: crypto.randomUUID(), role: 'assistant', kind: 'progress', content: event.text },
              ])
            } else if (event.type === 'tool_start') {
              log(`▶ ${event.name} ${shortInput(event.input)}`)
            } else if (event.type === 'tool_success') {
              log(`✓ ${event.name}`)
            } else if (event.type === 'tool_error') {
              log(`✗ ${event.name}: ${event.message}`)
            }
          },
        })
        // 会話履歴を永続化して次ターンへ引き継ぐ。
        conversationStore.setMessages(result.messages)

        if (result.content && result.status !== 'aborted' && result.status !== 'refused') {
          setMessages((cur) => [
            ...cur,
            { id: crypto.randomUUID(), role: 'assistant', content: result.content },
          ])
        } else if (result.status === 'aborted') {
          setMessages((cur) => [
            ...cur,
            { id: crypto.randomUUID(), role: 'assistant', kind: 'notice', content: '中断しました。' },
          ])
        } else if (result.status === 'refused') {
          setMessages((cur) => [
            ...cur,
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              kind: 'notice',
              content: '回答が拒否されました。',
            },
          ])
        }
      } catch (e) {
        setMessages((cur) => [
          ...cur,
          {
            id: crypto.randomUUID(),
            role: 'assistant',
            kind: 'notice',
            content: `エラー: ${String(e?.message ?? e)}`,
          },
        ])
      } finally {
        setIsRunning(false)
        abortRef.current = null
      }
    },
    [apiKey, isRunning, toolRegistry, layers, dataTables, model, maxTokens, log],
  )

  const handleAbort = useCallback(() => abortRef.current?.abort(), [])
  const handleResetChat = useCallback(() => {
    conversationStore.clear()
    setMessages([])
    setChatInput('')
    try {
      globalThis.localStorage?.removeItem(CHAT_VIEW_STORAGE)
    } catch {
      // 無視
    }
  }, [])

  // チャット入力欄の末尾にテキストを追記してフォーカス（レイヤー行の「@」用）。
  const appendToInput = useCallback((text) => {
    setChatInput((p) => p + (p && !p.endsWith(' ') ? ' ' : '') + text + ' ')
    window.requestAnimationFrame(() => chatInputRef.current?.focus())
  }, [])

  return {
    messages,
    isRunning,
    chatInput,
    setChatInput,
    chatInputRef,
    handleSubmit,
    handleAbort,
    handleResetChat,
    appendToInput,
  }
}
